/**
 * Levels 13–18 (Grades 7–9): bigger adventures — misty terraces, a friendly
 * volcano, a sunny glacier bay, candy cliffs, a pirate harbour and a village
 * in the treetops. Scenes get busier and more playful.
 */
import type { ThemePack } from '../types.ts';
import { CLOUD_WHITE, PARROTS, TRUNK } from './shared.ts';

const bambooSteps: ThemePack = {
  id: 'bamboo-steps',
  level: 13,
  name: { en: 'Bamboo Steps', af: 'Bamboestrappe' },
  tagline: { en: 'Climb the green terraces to the misty pagoda.', af: 'Klim die groen terrasse uit na die pagode in die mis.' },
  backgroundConcept:
    'Emerald terraces step up a hillside towards a red-and-jade pagoda on its own little hill, with misty blue peaks behind. Tall bamboo groves sway at both edges, blossom trees lean over the lowest terrace and pink petals drift on the breeze.',
  uiNotes: 'Pale jade panels with deep pine text, jade buttons and a blossom-pink accent. Bamboo bricks with darker nodes.',
  animationIdeas: ['Bamboo stalks sway gently', 'Blossom petals drift down', 'Clouds slide past the pagoda', 'Mist softens the far peaks'],
  soundNotes: 'D major pentatonic bamboo flute at a calm 76 bpm over an airy pad, with a trickling stream between the terraces.',
  palette: {
    skyTop: '#62c6f5',
    skyBottom: '#e6fbef',
    wellBg: '#ffffff5c',
    wellBorder: '#f0fff6cc',
    blockBg: '#fbfffa',
    blockBorder: '#4cc38a',
    blockText: '#10301f',
    brickA: '#b9cf6a',
    brickB: '#a3bc57',
    brickMortar: '#f2f7dc',
    uiPanel: '#f6fff9eb',
    uiText: '#10301f',
    uiMuted: '#4d6b5a',
    accent: '#f06595',
    buttonBg: '#11795a',
    buttonText: '#ffffff',
    good: '#23a45e',
    bad: '#e5625e',
  },
  brickStyle: 'bamboo',
  particles: { kind: 'petals', density: 0.45, color: '#ffc2d9' },
  scene: [
    { kind: 'sky', mid: '#c4f0f0', midAt: 0.55 },
    { kind: 'sun', x: 0.8, y: 0.12, radius: 0.06, color: '#fff3a0', glow: '#fffbe0', rays: 8, rayColor: '#fffbe6' },
    { kind: 'clouds', count: 4, yMin: 0.06, yMax: 0.26, size: 0.22, color: CLOUD_WHITE, shade: '#d9efe8', speed: 0.5, depth: 0.05 },
    { kind: 'ridges', shape: 'peaks', baseline: 0.5, height: 0.22, color: '#a9d8c4', roughness: 0.5, mist: 0.6, depth: 0.05 },
    {
      kind: 'structure',
      type: 'pagoda',
      x: 0.68,
      baseline: 0.52,
      size: 0.17,
      color: '#e8594a',
      accent: '#2f9e76',
      trim: '#ffd43b',
      mound: '#9fd6a4',
      depth: 0.2,
    },
    { kind: 'ridges', shape: 'terraces', baseline: 0.7, height: 0.14, color: '#8fd46e', stripes: '#b2e691', roughness: 0.35, depth: 0.25 },
    { kind: 'ridges', shape: 'terraces', baseline: 0.86, height: 0.1, color: '#7ccc5c', stripes: '#a2de80', roughness: 0.3, depth: 0.5 },
    {
      kind: 'trees',
      species: 'blossom',
      count: 2,
      baseline: 0.88,
      size: 0.22,
      spread: [0.5, 0.85],
      color: '#ffb3cf',
      trunk: '#8a5a44',
      accent: '#ff85b3',
      depth: 0.5,
    },
    {
      kind: 'trees',
      species: 'bamboo',
      count: 5,
      baseline: 0.93,
      size: 0.46,
      spread: [-0.06, 0.2],
      color: '#6cc24a',
      trunk: '#8fcf5a',
      accent: '#5e9e3a',
      sway: true,
      depth: 0.75,
    },
    {
      kind: 'trees',
      species: 'bamboo',
      count: 4,
      baseline: 0.93,
      size: 0.42,
      spread: [0.84, 1.06],
      color: '#6cc24a',
      trunk: '#8fcf5a',
      accent: '#5e9e3a',
      sway: true,
      depth: 0.75,
      seed: 9,
    },
    { kind: 'ground', top: 0.92, color: '#6cc257', texture: 'grass', accent: '#56a948', depth: 0.75 },
  ],
  audio: { key: 'D', scale: 'majorPentatonic', bpm: 76, lead: 'flute', pad: 'airy', ambience: 'stream' },
};

const volcanoIsland: ThemePack = {
  id: 'volcano-island',
  level: 14,
  name: { en: 'Volcano Island', af: 'Vulkaaneiland' },
  tagline: { en: 'A friendly volcano puffs candy-floss clouds.', af: "'n Vriendelike vulkaan blaas spookasem-wolkies." },
  backgroundConcept:
    'A tropical island in a sparkling sea with a cheerful volcano in the middle, puffing pink-and-peach smoke. Glowing orange lava trickles down its rocky top, jungle covers the lower slopes, palms lean over a white beach and parrots circle the peak. Bright and sunny — never scary.',
  uiNotes: 'Warm white panels with deep plum text, a tangerine block border, sea-teal buttons and a lava-orange accent. Warm clay bricks.',
  animationIdeas: ['Pastel smoke puffs rise and fade', 'The crater glows softly', 'Waves roll in and sparkle', 'Parrots circle the peak'],
  soundNotes: 'C mixolydian kalimba at a bouncy 100 bpm, warm pad, rolling surf.',
  palette: {
    skyTop: '#48c4f7',
    skyBottom: '#ffe9d6',
    wellBg: '#fffaf55c',
    wellBorder: '#fff3e8cc',
    blockBg: '#fffaf4',
    blockBorder: '#ff8f3f',
    blockText: '#2a1a2e',
    brickA: '#d98c6a',
    brickB: '#c77756',
    brickMortar: '#fde6d6',
    uiPanel: '#fffaf5e8',
    uiText: '#2a1a2e',
    uiMuted: '#6b5868',
    accent: '#ff6b2c',
    buttonBg: '#0b7285',
    buttonText: '#ffffff',
    good: '#2a9d5c',
    bad: '#e55a5a',
  },
  brickStyle: 'clay',
  particles: { kind: 'pollen', density: 0.35, color: '#ffe0b3' },
  scene: [
    { kind: 'sky', mid: '#bfeaff', midAt: 0.5 },
    { kind: 'sun', x: 0.15, y: 0.12, radius: 0.065, color: '#ffe45c', glow: '#fff2b3', rays: 10, rayColor: '#fff6d1' },
    { kind: 'clouds', count: 3, yMin: 0.06, yMax: 0.24, size: 0.2, color: CLOUD_WHITE, shade: '#ffe0d6', speed: 0.6, depth: 0.05 },
    { kind: 'water', top: 0.6, color: '#3ccfe0', deep: '#159ec4', foam: '#ffffff', waves: 0.6, sparkle: true, depth: 0.1 },
    {
      kind: 'volcano',
      x: 0.56,
      baseline: 0.68,
      width: 0.9,
      height: 0.42,
      color: '#c9977c',
      lava: '#ff8c42',
      smoke: '#ffd6e0',
      skirt: '#6fcf6a',
      shore: '#ffe4b0',
      depth: 0.1,
    },
    { kind: 'birds', style: 'parrot', count: 3, yMin: 0.12, yMax: 0.3, size: 0.05, colors: PARROTS, speed: 4, depth: 0.3 },
    { kind: 'ground', top: 0.83, color: '#ffe4b0', texture: 'sand', accent: '#f3c983', depth: 0.6 },
    {
      kind: 'trees',
      species: 'palm',
      count: 3,
      baseline: 0.86,
      size: 0.32,
      spread: [0.62, 1.06],
      color: '#55c25a',
      trunk: '#b07a4a',
      accent: '#8a5a2b',
      sway: true,
      depth: 0.6,
    },
    { kind: 'trees', species: 'fern', count: 4, baseline: 0.93, size: 0.11, spread: [-0.05, 0.32], color: '#4fb84f', trunk: '#3f8a3a', sway: true, depth: 0.7 },
  ],
  audio: { key: 'C', scale: 'mixolydian', bpm: 100, lead: 'kalimba', pad: 'warm', ambience: 'waves' },
};

const glacierBay: ThemePack = {
  id: 'glacier-bay',
  level: 15,
  name: { en: 'Glacier Bay', af: 'Gletserbaai' },
  tagline: { en: 'Sparkling icebergs under a sunny sky.', af: "Glinsterende ysberge onder 'n sonnige hemel." },
  backgroundConcept:
    'A sunny polar bay: snow-capped peaks, minty-blue water dotted with gleaming icebergs, snowy pine trees on a white shore and soft snowflakes twinkling in the sunshine. Crisp, clean and cheerful — blue skies, never grey.',
  uiNotes: 'Snow-white panels with deep ice-blue text, a cyan block border, royal-blue buttons and a warm coral accent so it never feels cold. Ice bricks.',
  animationIdeas: ['Icebergs bob very gently', 'Snowflakes drift and twinkle', 'Sparkles glint on the water', 'Clouds slide over the peaks'],
  soundNotes: 'B-flat major bells at a slow 80 bpm with a glass pad and a soft, high wind.',
  palette: {
    skyTop: '#58bff5',
    skyBottom: '#e3f6ff',
    wellBg: '#ffffff5c',
    wellBorder: '#ffffffcc',
    blockBg: '#fafdff',
    blockBorder: '#3bc9db',
    blockText: '#0f2a44',
    brickA: '#cfefff',
    brickB: '#b5e3fa',
    brickMortar: '#ffffff',
    uiPanel: '#ffffffeb',
    uiText: '#0f2a44',
    uiMuted: '#52698a',
    accent: '#ff7b6b',
    buttonBg: '#1c64b8',
    buttonText: '#ffffff',
    good: '#1f9e62',
    bad: '#e25563',
  },
  brickStyle: 'ice',
  particles: { kind: 'snow', density: 0.5, color: '#ffffff' },
  scene: [
    { kind: 'sky', mid: '#bfe6ff', midAt: 0.5 },
    { kind: 'sun', x: 0.8, y: 0.13, radius: 0.065, color: '#fff2a0', glow: '#fffbe0', rays: 12, rayColor: '#ffffff' },
    { kind: 'clouds', count: 4, yMin: 0.05, yMax: 0.25, size: 0.22, color: CLOUD_WHITE, shade: '#dcecff', speed: 0.5, depth: 0.05 },
    { kind: 'ridges', shape: 'peaks', baseline: 0.54, height: 0.28, color: '#b3d4ec', snow: '#ffffff', snowLine: 0.45, mist: 0.45, depth: 0.05 },
    {
      kind: 'ridges',
      shape: 'peaks',
      baseline: 0.62,
      height: 0.16,
      color: '#9cc7e4',
      snow: '#ffffff',
      snowLine: 0.55,
      roughness: 0.6,
      depth: 0.15,
      seed: 4,
    },
    { kind: 'water', top: 0.62, color: '#7fd9f0', deep: '#3fb3e0', foam: '#ffffff', waves: 0.3, sparkle: true, depth: 0.2 },
    { kind: 'icebergs', count: 4, waterline: 0.72, size: 0.14, color: '#ffffff', shade: '#bfe9ff', depth: 0.3 },
    { kind: 'ground', top: 0.85, color: '#fbfdff', rim: '#bcd9ee', texture: 'snow', accent: '#d7ecfb', depth: 0.7 },
    { kind: 'trees', species: 'pine', count: 4, baseline: 0.88, size: 0.22, spread: [-0.05, 0.32], color: '#3fa36b', trunk: TRUNK, accent: '#ffffff', depth: 0.7 },
    {
      kind: 'trees',
      species: 'pine',
      count: 3,
      baseline: 0.89,
      size: 0.18,
      spread: [0.74, 1.05],
      color: '#48ad73',
      trunk: TRUNK,
      accent: '#ffffff',
      depth: 0.7,
      seed: 12,
    },
  ],
  audio: { key: 'Bb', scale: 'major', bpm: 80, lead: 'bell', pad: 'glass', ambience: 'wind' },
};

const candyCliffs: ThemePack = {
  id: 'candy-cliffs',
  level: 16,
  name: { en: 'Candy Cliffs', af: 'Lekkergoedkranse' },
  tagline: { en: 'Sugar-frosted cliffs and lollipop forests.', af: 'Suikerversierde kranse en stokkielekker-woude.' },
  backgroundConcept:
    'A sweet-shop wonderland: strawberry-pink mesas and lavender hills dripping with white icing, mint-green slopes planted with swirly lollipop trees, a candy ground scattered with sprinkles and a rainbow over it all. Candy-floss clouds drift by.',
  uiNotes: 'Bubblegum-white panels with dark chocolate text, raspberry buttons and a mint accent. Candy-stripe bricks.',
  animationIdeas: ['Sprinkles tumble down like confetti', 'Candy-floss clouds drift past', 'Sun rays turn slowly', 'Everything gently glistens'],
  soundNotes: 'E-flat major pentatonic marimba at a perky 112 bpm — the fastest loop — with a glass pad and cheerful birdsong.',
  palette: {
    skyTop: '#7fd0ff',
    skyBottom: '#ffe0f0',
    wellBg: '#ffffff5c',
    wellBorder: '#fff0f8cc',
    blockBg: '#fffafd',
    blockBorder: '#ff7eb9',
    blockText: '#341a24',
    brickA: '#ffb3d1',
    brickB: '#a8e6cf',
    brickMortar: '#fff7fb',
    uiPanel: '#fff9fce8',
    uiText: '#341a24',
    uiMuted: '#76586a',
    accent: '#12b886',
    buttonBg: '#c2185b',
    buttonText: '#ffffff',
    good: '#1f9e62',
    bad: '#e5484d',
  },
  brickStyle: 'candy',
  particles: { kind: 'confetti', density: 0.45, color: '#ff8fc7' },
  scene: [
    { kind: 'sky', mid: '#d7ecff', midAt: 0.5 },
    { kind: 'sun', x: 0.2, y: 0.12, radius: 0.06, color: '#fff0a0', glow: '#fff7d6', rays: 10, rayColor: '#ffe3f1' },
    { kind: 'rainbow', x: 0.58, y: 0.66, radius: 0.4, width: 0.07, opacity: 0.45 },
    { kind: 'clouds', count: 4, yMin: 0.06, yMax: 0.3, size: 0.22, color: CLOUD_WHITE, shade: '#ffd9ec', speed: 0.6, depth: 0.05 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.6, height: 0.16, color: '#c7b8ff', icing: '#ffffff', mist: 0.3, depth: 0.1 },
    { kind: 'ridges', shape: 'mesa', baseline: 0.72, height: 0.2, color: '#ff9ec7', stripes: '#ffb8d6', icing: '#fff4fa', roughness: 0.4, depth: 0.25 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.82, height: 0.07, color: '#9ff0cf', icing: '#ffffff', roughness: 0.3, depth: 0.4 },
    { kind: 'trees', species: 'lollipop', count: 5, baseline: 0.83, size: 0.17, color: '#ff6fa8', trunk: '#ffffff', accent: '#fff0f6', depth: 0.4 },
    {
      kind: 'ground',
      top: 0.89,
      color: '#ffd1e6',
      rim: '#ff9cc8',
      texture: 'candy',
      accent: '#ffffff',
      flowers: ['#ff6b6b', '#ffd43b', '#4dabf7', '#69db7c', '#b197fc'],
      depth: 0.7,
    },
    {
      kind: 'trees',
      species: 'lollipop',
      count: 2,
      baseline: 0.97,
      size: 0.24,
      spread: [-0.04, 0.2],
      color: '#7ad7f0',
      trunk: '#ffffff',
      accent: '#fff9db',
      depth: 0.7,
      seed: 6,
    },
  ],
  audio: { key: 'Eb', scale: 'majorPentatonic', bpm: 112, lead: 'marimba', pad: 'glass', ambience: 'birds' },
};

const pirateHarbour: ThemePack = {
  id: 'pirate-harbour',
  level: 17,
  name: { en: 'Pirate Harbour', af: 'Seerowershawe' },
  tagline: { en: 'Ahoy! Treasure glints in the friendly harbour.', af: 'Ahoi! Skatte glinster in die vriendelike hawe.' },
  backgroundConcept:
    'A sunny harbour: a big friendly galleon with striped sails bobs at anchor, a red-and-white lighthouse stands on a grassy headland, palms shade a golden beach, gulls wheel overhead and signal flags flutter. Gold glints everywhere — treasure is near!',
  uiNotes: 'Parchment panels with deep navy text, a gold block border, navy buttons and a treasure-gold accent. Weathered plank bricks.',
  animationIdeas: ['The galleon rocks on the swell', 'Gulls glide in lazy loops', 'Signal flags flutter', 'Gold sparkles twinkle'],
  soundNotes: 'D dorian plucked strings, like a sea-shanty mandolin, at 96 bpm with a warm pad and lapping harbour waves.',
  palette: {
    skyTop: '#4dbdf5',
    skyBottom: '#d8f3ff',
    wellBg: '#fffdf55c',
    wellBorder: '#fff8e6cc',
    blockBg: '#fffcf2',
    blockBorder: '#f2b705',
    blockText: '#132238',
    brickA: '#c89a6a',
    brickB: '#b38457',
    brickMortar: '#f7ead3',
    uiPanel: '#fffaeeeb',
    uiText: '#132238',
    uiMuted: '#5a6475',
    accent: '#f2a900',
    buttonBg: '#1d4e89',
    buttonText: '#ffffff',
    good: '#23a15a',
    bad: '#e05a52',
  },
  brickStyle: 'wood',
  particles: { kind: 'sparkles', density: 0.4, color: '#ffe066' },
  scene: [
    { kind: 'sky', mid: '#a9e2ff', midAt: 0.5 },
    { kind: 'sun', x: 0.5, y: 0.1, radius: 0.065, color: '#ffe45c', glow: '#fff3b0', rays: 12, rayColor: '#fff7cf' },
    { kind: 'clouds', count: 4, yMin: 0.05, yMax: 0.26, size: 0.22, color: CLOUD_WHITE, shade: '#d3ecfb', speed: 0.8, depth: 0.05 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.56, height: 0.08, color: '#9bd6a8', roughness: 0.4, mist: 0.4, depth: 0.05 },
    { kind: 'water', top: 0.56, color: '#3dc3e6', deep: '#1a8fc4', foam: '#ffffff', waves: 0.5, sparkle: true, depth: 0.15 },
    { kind: 'cliffs', side: 'right', width: 0.28, top: 0.34, color: '#c9a47a', stripes: '#b38d64', moss: '#6fcf6a', depth: 0.25 },
    {
      kind: 'structure',
      type: 'lighthouse',
      x: 0.87,
      baseline: 0.355,
      size: 0.22,
      color: '#ffffff',
      accent: '#e8453c',
      trim: '#ffd43b',
      depth: 0.25,
    },
    { kind: 'birds', style: 'gull', count: 5, yMin: 0.1, yMax: 0.34, size: 0.045, colors: ['#ffffff'], speed: 3, depth: 0.3 },
    {
      kind: 'structure',
      type: 'ship',
      x: 0.42,
      baseline: 0.74,
      size: 0.34,
      color: '#9c6644',
      accent: '#fffaf0',
      trim: '#e8453c',
      bob: true,
      depth: 0.4,
    },
    { kind: 'ground', top: 0.86, color: '#ffe2a8', texture: 'sand', accent: '#f0c47a', depth: 0.7 },
    {
      kind: 'trees',
      species: 'palm',
      count: 2,
      baseline: 0.89,
      size: 0.3,
      spread: [-0.05, 0.24],
      color: '#55c25a',
      trunk: '#b07a4a',
      accent: '#8a5a2b',
      sway: true,
      depth: 0.7,
    },
    { kind: 'bunting', y: 0.03, sag: 0.05, colors: ['#e8453c', '#ffd43b', '#1d4e89', '#ffffff', '#2fb35a'], string: '#8b5a3c', depth: 0.9 },
  ],
  audio: { key: 'D', scale: 'dorian', bpm: 96, lead: 'pluck', pad: 'warm', ambience: 'waves' },
};

const treetopVillage: ThemePack = {
  id: 'treetop-village',
  level: 18,
  name: { en: 'Treetop Village', af: 'Boomtopdorpie' },
  tagline: { en: 'Rope bridges between cosy treehouses.', af: 'Toubrûe tussen gesellige boomhuisies.' },
  backgroundConcept:
    'A village high in giant trees: round-windowed treehouses with bright roofs sit on sturdy platforms, a swaying rope bridge links them, vines hang from the canopy and a sunny valley with a misty river lies far below.',
  uiNotes: 'Leafy cream panels with deep bark-brown text, forest-green buttons and a sunny orange accent. Mossy plank bricks.',
  animationIdeas: ['Leaves spin down from the canopy', 'Vines sway on the breeze', 'Sunbeams shimmer through the leaves', 'Low clouds drift through the valley'],
  soundNotes: 'F major pentatonic kalimba at 90 bpm, warm pad, birdsong and rustling leaves.',
  palette: {
    skyTop: '#58c6f0',
    skyBottom: '#e3f8d8',
    wellBg: '#ffffff5c',
    wellBorder: '#f6ffeecc',
    blockBg: '#fffdf6',
    blockBorder: '#8bc34a',
    blockText: '#2b1d0e',
    brickA: '#9bb86a',
    brickB: '#86a457',
    brickMortar: '#eef3d8',
    uiPanel: '#fffdf3eb',
    uiText: '#2b1d0e',
    uiMuted: '#6a5a45',
    accent: '#ff922b',
    buttonBg: '#2b7a3b',
    buttonText: '#ffffff',
    good: '#2a9d5c',
    bad: '#e0605a',
  },
  brickStyle: 'mossy',
  particles: { kind: 'leaves', density: 0.45, color: '#8fd46b' },
  scene: [
    { kind: 'sky', mid: '#bff0e0', midAt: 0.55 },
    { kind: 'lightRays', x: 0.7, count: 6, spread: 0.8, color: '#fff8d0', opacity: 0.3 },
    { kind: 'clouds', count: 3, yMin: 0.08, yMax: 0.25, size: 0.2, color: CLOUD_WHITE, shade: '#dcf1e6', speed: 0.5, depth: 0.05 },
    { kind: 'ridges', shape: 'rolling', baseline: 0.72, height: 0.12, color: '#a8dca0', roughness: 0.5, mist: 0.5, depth: 0.05 },
    { kind: 'clouds', count: 3, yMin: 0.66, yMax: 0.74, size: 0.3, color: CLOUD_WHITE, shade: '#e1f2e6', speed: 0.3, style: 'long', depth: 0.1, seed: 8 },
    { kind: 'trees', species: 'jungle', count: 4, baseline: 0.84, size: 0.5, color: '#6cc27c', trunk: '#8a6a4a', depth: 0.2 },
    {
      kind: 'bridge',
      from: { x: 0.2, y: 0.97, rise: 0.29 },
      to: { x: 0.79, y: 0.93, rise: 0.25 },
      sag: 0.05,
      rope: '#8b5a3c',
      plank: '#c89a6a',
      depth: 0.5,
    },
    {
      kind: 'structure',
      type: 'treehouse',
      x: 0.2,
      baseline: 0.97,
      size: 0.52,
      color: '#c08552',
      accent: '#e8573c',
      trim: '#4caf50',
      depth: 0.5,
    },
    {
      kind: 'structure',
      type: 'treehouse',
      x: 0.79,
      baseline: 0.93,
      size: 0.46,
      color: '#c89560',
      accent: '#f59f00',
      trim: '#43a047',
      depth: 0.5,
    },
    { kind: 'ground', top: 0.94, color: '#5cb85c', texture: 'moss', accent: '#4a9e4a', depth: 0.8 },
    { kind: 'trees', species: 'fern', count: 6, baseline: 0.97, size: 0.12, color: '#57bb4f', trunk: '#3f8a3a', sway: true, depth: 0.8 },
    { kind: 'vines', count: 7, length: 0.3, color: '#3f8f3a', leaf: '#62c054', flower: '#ffd43b', canopy: '#3fa04a', depth: 0.9 },
  ],
  audio: { key: 'F', scale: 'majorPentatonic', bpm: 90, lead: 'kalimba', pad: 'warm', ambience: 'birds' },
};

export const LEVELS_13_18: ThemePack[] = [bambooSteps, volcanoIsland, glacierBay, candyCliffs, pirateHarbour, treetopVillage];
